import type {
  ModerationDataStage,
  ModerationFeedback,
  ModerationFeedbackAcceptanceResult,
  ModerationFeedbackList,
  ModerationProductResult,
  ModerationResult as ModerationStageResultModel,
  ModerationReviewApproveResult,
  ModerationReviewQueue,
  ModerationReviewQueueCounts,
  ModerationReviewRejectResult,
  ModerationViolation,
} from "@sakura-tech-lab/dashboard-api";

import { reviewApi } from "@/services/dashboard-api.client";

// Re-export generated response models under the names existing components use.
export type { ModerationDataStage, ModerationViolation };
export type ModerationStageResult = ModerationStageResultModel;
export type ModerationResult = ModerationProductResult;
export type ModerationFeedbackItem = ModerationFeedback;
export type ReviewQueueResponse = ModerationReviewQueue;
export type ReviewQueueCounts = ModerationReviewQueueCounts;
export type ApproveResponse = ModerationReviewApproveResult;
export type RejectResponse = ModerationReviewRejectResult;
export type FeedbackListResponse = ModerationFeedbackList;
export type AcceptFeedbackResponse = ModerationFeedbackAcceptanceResult;

export const ReviewService = {
  queue: async (
    catalogueId: string,
    query: {
      review_status?: "pending" | "approved" | "rejected" | "all";
      severity?: "low" | "medium" | "high" | "critical";
      slug?: string;
      order?: "asc" | "desc";
      limit?: number;
      offset?: number;
    } = {},
  ) => {
    const response = await reviewApi.listReviewQueue({
      catalogueId,
      reviewStatus: query.review_status ?? "pending",
      severity: query.severity,
      slug: query.slug,
      order: query.order,
      limit: query.limit,
      offset: query.offset,
    });
    return response.data;
  },

  queueCounts: async (catalogueId: string) => {
    const response = await reviewApi.getReviewQueueCounts({ catalogueId });
    return response.data;
  },

  getResult: async (catalogueId: string, productId: string) => {
    const response = await reviewApi.getReviewResult({ catalogueId, productId });
    return response.data;
  },

  approve: async (
    catalogueId: string,
    productId: string,
    input: {
      actor?: string;
      data_stage: ModerationDataStage;
      rule_slugs: string[];
      feedback?: Array<{ slug: string; text: string; retained_image_urls?: string[] }>;
    },
  ) => {
    const response = await reviewApi.approveReviewResult({
      catalogueId,
      productId,
      moderationReviewApproveRequest: {
        actor: input.actor,
        dataStage: input.data_stage,
        ruleSlugs: input.rule_slugs,
        feedback: input.feedback?.map((item) => ({
          slug: item.slug,
          text: item.text,
          retainedImageUrls: item.retained_image_urls,
        })),
      },
    });
    return response.data;
  },

  reject: async (
    catalogueId: string,
    productId: string,
    input: { data_stage: ModerationDataStage; actor?: string },
  ) => {
    const response = await reviewApi.rejectReviewResult({
      catalogueId,
      productId,
      moderationReviewDecisionRequest: {
        actor: input.actor,
        dataStage: input.data_stage,
      },
    });
    return response.data;
  },

  listFeedback: async (
    catalogueId: string,
    status: "pending" | "accepted" | "rejected" | "all" = "pending",
  ) => {
    const response = await reviewApi.listReviewFeedback({ catalogueId, status });
    return response.data;
  },

  acceptFeedback: async (catalogueId: string, feedbackId: string, actor?: string) => {
    const response = await reviewApi.acceptReviewFeedback({
      catalogueId,
      feedbackId,
      requestBody: { actor },
    });
    return response.data;
  },

  rejectFeedback: async (catalogueId: string, feedbackId: string, actor?: string) => {
    const response = await reviewApi.rejectReviewFeedback({
      catalogueId,
      feedbackId,
      requestBody: { actor },
    });
    return response.data;
  },
};
